import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { Star, MapPin, Check, Loader2, Calendar, Users } from "lucide-react";
import { toast } from "sonner";

export default function HotelDetail() {
  const { slug } = useParams();
  const [h, setH] = useState(null);
  const [room, setRoom] = useState(null);
  const [form, setForm] = useState({ check_in: "", check_out: "", guests: 2, guest_name: "", guest_email: "" });
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/hotels/${slug}`).then(({ data }) => { setH(data); setRoom(data.rooms[0]); }).catch(() => {}); }, [slug]);
  if (!h) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-emerald" size={32} /></div>;

  const nights = form.check_in && form.check_out ? Math.max(1, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 864e5)) : 1;
  const total = (room?.price || 0) * nights;

  const book = async () => {
    if (!form.check_in || !form.check_out || !form.guest_name) { toast.error("Please fill in dates and guest name"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        hotel_slug: slug, room_name: room.name, check_in: form.check_in, check_out: form.check_out,
        guests: Number(form.guests), nights, total, guest_name: form.guest_name, guest_email: form.guest_email || null,
      });
      setRef(data.ref); toast.success("Booking confirmed!");
    } catch { toast.error("Booking failed"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-sm text-muted-foreground mb-4"><Link to="/hotels" className="hover:text-emerald">Hotels</Link> / <span className="text-ink">{h.name}</span></nav>
      <div className="grid md:grid-cols-2 gap-3 rounded-3xl overflow-hidden">
        <img src={h.images[0]} alt={h.name} className="w-full h-72 md:h-96 object-cover" />
        <img src={h.images[1] || h.images[0]} alt="" className="w-full h-72 md:h-96 object-cover" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <div><h1 className="font-display text-3xl font-bold">{h.name}</h1><div className="text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={16} /> {h.parish} · {h.type}</div></div>
            <span className="flex items-center gap-1 font-semibold"><Star size={18} className="fill-gold text-gold" /> {h.rating} <span className="text-muted-foreground text-sm">({h.reviews})</span></span>
          </div>
          <p className="text-slate-700 mt-4 leading-relaxed">{h.blurb}</p>

          <h3 className="font-display font-bold text-lg mt-8 mb-3">Amenities</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {h.amenities.map((a) => <div key={a} className="flex items-center gap-2 text-sm bg-secondary/50 rounded-lg px-3 py-2"><Check size={15} className="text-emerald" /> {a}</div>)}
          </div>

          <h3 className="font-display font-bold text-lg mt-8 mb-3">Choose your room</h3>
          <div className="space-y-3">
            {h.rooms.map((r) => (
              <button key={r.name} onClick={() => setRoom(r)} className={`w-full flex items-center justify-between rounded-xl border-2 p-4 text-left transition-colors ${room?.name === r.name ? "border-emerald bg-emerald/5" : "border-border"}`} data-testid="hotel-room-option">
                <div><div className="font-semibold">{r.name}</div><div className="text-sm text-muted-foreground">{r.beds} · sleeps {r.sleeps}</div></div>
                <div className="font-display font-bold">{jmd(r.price)}<span className="text-xs font-normal text-muted-foreground"> /night</span></div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 h-fit sticky top-28">
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3" data-testid="sample-listing-notice"><strong>Sample listing – not bookable.</strong> This is an illustrative example. Browse real, live-priced stays at <Link to="/travel" className="font-bold underline">Travel &amp; Stays</Link>.</div>
          {ref ? (
            <div className="text-center py-4">
              <Check size={44} className="mx-auto text-emerald mb-3" />
              <h3 className="font-display font-bold text-lg">Booking Confirmed</h3>
              <p className="text-sm text-muted-foreground mt-1">Reference <span className="font-mono-sn font-semibold text-ink">{ref}</span></p>
              <p className="text-sm mt-3">{room.name} · {nights} night(s)</p>
              <div className="font-display font-bold text-xl mt-2">{jmd(total)}</div>
              <Link to="/hotels" className="inline-block mt-4 text-emerald font-semibold">Browse more stays →</Link>
            </div>
          ) : (
            <>
              <div className="font-display font-bold text-2xl mb-1">{jmd(room?.price || 0)}<span className="text-sm font-normal text-muted-foreground"> / night</span></div>
              <div className="text-sm text-muted-foreground mb-4">{room?.name}</div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> Check-in</label><input type="date" value={form.check_in} onChange={(e) => setForm((f) => ({ ...f, check_in: e.target.value }))} className="w-full rounded-lg border border-border px-2 py-2 text-sm mt-1" data-testid="booking-checkin" /></div>
                  <div><label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> Check-out</label><input type="date" value={form.check_out} onChange={(e) => setForm((f) => ({ ...f, check_out: e.target.value }))} className="w-full rounded-lg border border-border px-2 py-2 text-sm mt-1" data-testid="booking-checkout" /></div>
                </div>
                <div><label className="text-xs text-muted-foreground flex items-center gap-1"><Users size={12} /> Guests</label><input type="number" min={1} value={form.guests} onChange={(e) => setForm((f) => ({ ...f, guests: e.target.value }))} className="w-full rounded-lg border border-border px-3 py-2 text-sm mt-1" /></div>
                <input value={form.guest_name} onChange={(e) => setForm((f) => ({ ...f, guest_name: e.target.value }))} placeholder="Guest name" className="w-full rounded-lg border border-border px-3 py-2 text-sm" data-testid="booking-name" />
                <input value={form.guest_email} onChange={(e) => setForm((f) => ({ ...f, guest_email: e.target.value }))} placeholder="Email (optional)" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
              </div>
              <div className="border-t border-border mt-4 pt-4 flex justify-between font-display font-bold"><span>{nights} night(s)</span><span>{jmd(total)}</span></div>
              <button disabled className="w-full mt-4 bg-black/20 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 cursor-not-allowed" data-testid="hotel-book-now-button">
                Sample listing – not bookable
              </button>
              <p className="text-xs text-muted-foreground text-center mt-2">Real stays with live pricing are on <Link to="/travel" className="underline">Travel &amp; Stays</Link></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
